import get from "lodash.get";
import IntlUtils from "services/intl/utils";

import moment from "moment";
import { FormattedMessage } from "react-intl";
import CommonUtils from "services/common/utils";
import GlobalFiltersUtils from "services/globalFilters/utils";
import MapUtils from "services/map/utils";
import Abilities from "services/permissions/abilities";
import ParkkiTheme from "themes/ParkkiTheme";
import MapConfig from "services/map/MapConfig";

class PedestrianStatisticsUtils {
  static getNormalAbsoluteValue = (current, previous) => {
    if (previous === 0) {
      return 0;
    }
    return Math.abs(Math.round(((current - previous) / previous) * 100));
  };

  static getWay = (current, previous) => {
    const compute = current - previous;
    switch (true) {
      case compute < 0:
        return -1;
      case compute > 0:
        return 1;
      default:
        return 0;
    }
  };

  static getColor = (current, previous) => {
    return current - previous < 0 ? "red" : "green";
  };

  static getWayByValue = (value) => {
    switch (true) {
      case value < 0:
        return -1;
      case value > 0:
        return 1;
      default:
        return 0;
    }
  };

  static getColorByValue = (value) => {
    return value < 0 ? "red" : "green";
  };

  static getAbsValue = (value) => {
    if (value === null || value === undefined) return null;

    return Math.abs(value);
  };

  static getAbsDiff = (a, b) => {
    return Math.abs(a - b);
  };

  static getQueueAreasMaxValue = (data) => {
    if (!data) {
      return 0;
    }

    const result =
      Math.ceil(
        Math.max(
          ...Object.values(data ?? {}).map((data) => {
            // eslint-disable-next-line no-unused-vars
            const [_, value] = Object.entries(data).flat();
            return Math.max(
              ...Object.values(value.real_time),
              ...Object.values(value.previsional)
            );
          })
        ) / 5
      ) * 5;

    return result === 0 ? 5 : result;
  };

  static getDataByFiveMinutesRange(data, from, to) {
    if (typeof data !== "object") {
      return Object.values(data);
    }

    const result = Object.keys(data).reduce((currentResult, currentKey) => {
      const currentKeyHours = parseInt(currentKey?.split(":")[0], 10);
      const currentKeyMinutes = parseInt(currentKey?.split(":")[1], 10);

      if (
        (currentKeyHours >= from && currentKeyHours < to) ||
        (currentKeyHours === to && currentKeyMinutes === 0)
      ) {
        return {
          ...currentResult,
          [currentKey]: data[currentKey],
        };
      }

      return currentResult;
    }, {});

    return Object.values(result);
  }

  /**
   * Formats an array of seconds to an array of minutes using moment.
   * @param {number[]} arr - Array of seconds
   * @returns {number[]} - Array of minutes
   */
  static transformSecondsToMinutes = (arr) => {
    return arr.map((x) => {
      if (x !== null) {
        return Math.round(moment.duration(x, "seconds").asMinutes());
      }

      return x;
    });
  };

  static getQueueWaitingTimeChart = (pedestrianStatistics, intl) => {
    const isHourModeSelected =
      pedestrianStatistics?.filters
        ?.isPedestrianQueueDailyWaitingTimeChartTimeRangeByHour === true;
    const from =
      pedestrianStatistics.filters.queueWaitingTimeRange.fromHour / 3600;
    const to = pedestrianStatistics.filters.queueWaitingTimeRange.toHour / 3600;
    const isOnlyOneQueueAreaSelected =
      Object.values(pedestrianStatistics?.queue?.dailyWaitingTime?.data ?? {})
        .length === 1;
    const realTimeValuesUnfiltered = isOnlyOneQueueAreaSelected
      ? Object.values(
          Object.values(
            pedestrianStatistics.queue.dailyWaitingTime.data?.[0] ?? {}
          )?.[0]?.real_time ?? {}
        )
      : [];
    const numberOfDataTicksOnGraphBetweenFromAndTo =
      (to * 60) / 5 - (from * 60) / 5 + 1;

    return {
      options: {
        maintainAspectRatio: false,
        responsive: true,
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 12,
          },
        },
        tooltips: {
          enabled: true,
          mode: "index",
          callbacks: {
            title: (tooltipItems, data) =>
              `${
                intl.messages[
                  "pedestrianStatisticsScene.chart.tooltipTitlePrefix"
                ]
              } ${get(data, "labels", {})[tooltipItems[0].index]}`,
            label: (
              tooltipItem = { datasetIndex: null, index: null },
              data = { datasets: [] }
            ) => {
              const { datasetIndex, index } = tooltipItem;
              const { datasets } = data;
              const currentDataset =
                datasetIndex >= 0 && datasets[datasetIndex];
              const nbPedestrians =
                currentDataset &&
                currentDataset.data &&
                currentDataset.data[index];

              return `${datasets[datasetIndex].label}: ${nbPedestrians} min`;
            },
          },
        },
        scales: {
          xAxes: [
            {
              categoryPercentage: 1.0,
              barPercentage: 0.95,
              ticks: {
                autoSkip: true,
                maxTicksLimit: 10,
              },
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                min: 0,
                callback: function (label, index, labels) {
                  if (Math.floor(Number(label)) === Number(label)) {
                    return `${label} min`;
                  }
                },
              },
            },
          ],
        },
      },
      data: {
        toggle: Abilities.ability.can(
          "read",
          "pedestrian_stats.queue.dailyWaitingTime"
        ),
        labels: isHourModeSelected
          ? Array.from({ length: 24 }, (_, x) => `${x}h`)
          : // Calculation to display the correct time range on the x-axis based on filters
            Array.from(
              { length: numberOfDataTicksOnGraphBetweenFromAndTo },
              (_, x) => {
                return moment()
                  .hours(from)
                  .minutes(x * 5)
                  .format("HH:mm");
              }
            ),
        datasets: [
          ...Object.values(
            pedestrianStatistics?.queue?.dailyWaitingTime?.data ?? {}
          ).map((data) => {
            if (!data) return null;
            const [key] = Object.entries(data).flat();
            const area = pedestrianStatistics.map.selectedLayers.find(
              (area) => area.area_id === key
            );

            const color =
              area?.options.selection_color ||
              MapConfig?.flow?.point?.fillColor?.selected;

            let realTime = isHourModeSelected
              ? this.transformSecondsToMinutes(
                  Object.values(data[key].real_time)
                )
              : this.transformSecondsToMinutes(
                  this.getDataByFiveMinutesRange(data[key].real_time, from, to)
                );

            return {
              type: "line",
              label: area?.long_name ?? key,
              data: realTime,
              fill: false,
              fillOpacity: 0.4,
              borderWidth: 2,
              borderColor: color,
              backgroundColor: color,
              pointBorderColor: color,
              pointBackgroundColor: color,
              pointHoverBackgroundColor: color,
              pointHoverBorderColor: color,
            };
          }),
          isOnlyOneQueueAreaSelected
            ? {
                label:
                  intl.messages["pedestrianStatisticsScene.chart.previsional"],
                data: isHourModeSelected
                  ? this.transformSecondsToMinutes(
                      Object.values(
                        Object.values(
                          pedestrianStatistics.queue.dailyWaitingTime.data[0]
                        )[0].previsional
                      )
                    )
                  : this.transformSecondsToMinutes(
                      this.getDataByFiveMinutesRange(
                        Object.values(
                          pedestrianStatistics?.queue?.dailyWaitingTime
                            ?.data?.[0] ?? {}
                        )?.[0]?.previsional ?? {},
                        from,
                        to
                      )
                    ),
                borderColor: ParkkiTheme.colors.turquoise,
                backgroundColor:
                  PedestrianStatisticsUtils.getBackgroundColorArray(
                    isHourModeSelected
                      ? realTimeValuesUnfiltered.filter((x) => x !== null)
                          .length - 1
                      : realTimeValuesUnfiltered.filter((x) => x !== null)
                          .length -
                          (from * 60) / 5, // Gives us the number of data ticks passed since the current hour
                    isHourModeSelected ? "HOUR" : "5MIN"
                  ),
              }
            : null,
        ].filter((x) => x !== null),
      },
    };
  };

  static getQueueAttendanceChart = (pedestrianStatistics, intl) => {
    const isHourModeSelected =
      pedestrianStatistics?.filters
        ?.isPedestrianQueueAffluenceChartTimeRangeByHour === true;
    const from = pedestrianStatistics.filters.queueRange.fromHour / 3600;
    const to = pedestrianStatistics.filters.queueRange.toHour / 3600;
    const isOnlyOneQueueAreaSelected =
      Object.values(pedestrianStatistics?.queue?.dailyAttendance?.data ?? {})
        .length === 1;
    const realTimeValuesUnfiltered = isOnlyOneQueueAreaSelected
      ? Object.values(
          Object.values(
            pedestrianStatistics.queue.dailyAttendance.data?.[0] ?? {}
          )?.[0]?.real_time ?? {}
        )
      : [];
    const numberOfDataTicksOnGraphBetweenFromAndTo =
      (to * 60) / 5 - (from * 60) / 5 + 1;

    return {
      options: {
        maintainAspectRatio: false,
        responsive: true,
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 12,
          },
        },
        tooltips: {
          enabled: true,
          mode: "index",
          callbacks: {
            title: (tooltipItems, data) =>
              `${
                intl.messages[
                  "pedestrianStatisticsScene.chart.tooltipTitlePrefix"
                ]
              } ${get(data, "labels", {})[tooltipItems[0].index]}`,
            label: (
              tooltipItem = { datasetIndex: null, index: null },
              data = { datasets: [] }
            ) => {
              const { datasetIndex, index } = tooltipItem;
              const { datasets } = data;
              const currentDataset =
                datasetIndex >= 0 && datasets[datasetIndex];
              const nbPedestrians =
                currentDataset &&
                currentDataset.data &&
                currentDataset.data[index];

              return `${datasets[datasetIndex].label}: ${nbPedestrians} ${
                intl.messages[
                  `pedestrianStatisticsScene.chart.pedestrian${
                    nbPedestrians > 1 ? "s" : ""
                  }`
                ]
              }`;
            },
          },
        },
        scales: {
          xAxes: [
            {
              categoryPercentage: 1.0,
              barPercentage: 0.95,
              ticks: {
                autoSkip: true,
                maxTicksLimit: 10,
              },
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                min: 0,
                callback: function (label, index, labels) {
                  if (Math.floor(Number(label)) === Number(label)) {
                    return label;
                  }
                },
              },
            },
          ],
        },
      },
      data: {
        toggle: Abilities.ability.can(
          "read",
          "pedestrian_stats.queue.dailyAttendance"
        ),
        labels: isHourModeSelected
          ? Array.from({ length: 24 }, (_, x) => `${x}h`)
          : // Calculation to display the correct time range on the x-axis based on filters
            Array.from(
              { length: numberOfDataTicksOnGraphBetweenFromAndTo },
              (_, x) => {
                return moment()
                  .hours(from)
                  .minutes(x * 5)
                  .format("HH:mm");
              }
            ),
        datasets: [
          ...Object.values(
            pedestrianStatistics?.queue?.dailyAttendance?.data ?? {}
          ).map((data) => {
            if (!data) return null;
            const [key] = Object.entries(data).flat();
            const area = pedestrianStatistics.map.selectedLayers.find(
              (area) => area.area_id === key
            );
            const color =
              area?.options.selection_color ||
              MapConfig?.flow?.point?.fillColor?.selected;

            let realTime = isHourModeSelected
              ? Object.values(data[key].real_time)
              : this.getDataByFiveMinutesRange(data[key].real_time, from, to);

            return {
              type: "line",
              label: area?.long_name ?? key,
              data: realTime,
              fill: false,
              fillOpacity: 0.4,
              borderWidth: 2,
              borderColor: color,
              backgroundColor: color,
              pointBorderColor: color,
              pointBackgroundColor: color,
              pointHoverBackgroundColor: color,
              pointHoverBorderColor: color,
            };
          }),
          isOnlyOneQueueAreaSelected
            ? {
                label:
                  intl.messages["pedestrianStatisticsScene.chart.previsional"],
                data: isHourModeSelected
                  ? Object.values(
                      Object.values(
                        pedestrianStatistics.queue.dailyAttendance.data[0]
                      )[0].previsional
                    )
                  : this.getDataByFiveMinutesRange(
                      Object.values(
                        pedestrianStatistics?.queue?.dailyAttendance
                          ?.data?.[0] ?? {}
                      )?.[0]?.previsional ?? {},
                      from,
                      to
                    ),
                borderColor: ParkkiTheme.colors.turquoise,
                backgroundColor:
                  PedestrianStatisticsUtils.getBackgroundColorArray(
                    isHourModeSelected
                      ? realTimeValuesUnfiltered.filter((x) => x !== null)
                          .length - 1
                      : realTimeValuesUnfiltered.filter((x) => x !== null)
                          .length -
                          (from * 60) / 5, // Gives us the number of data ticks passed since the current hour
                    isHourModeSelected ? "HOUR" : "5MIN"
                  ),
              }
            : null,
        ].filter((x) => x !== null),
      },
    };
  };

  static getQueueAttendanceMaxChart = (pedestrianStatistics, intl) => {
    const isHourModeSelected =
      pedestrianStatistics?.filters
        ?.isPedestrianQueueAffluenceMaxChartTimeRangeByHour === true;
    const from = pedestrianStatistics.filters.queueMaxRange.fromHour / 3600;
    const to = pedestrianStatistics.filters.queueMaxRange.toHour / 3600;
    const isOnlyOneQueueAreaSelected =
      Object.values(pedestrianStatistics?.queue?.dailyAttendanceMax?.data ?? {})
        .length === 1;
    const realTimeValuesUnfiltered = isOnlyOneQueueAreaSelected
      ? Object.values(
          Object.values(
            pedestrianStatistics.queue.dailyAttendanceMax.data?.[0] ?? {}
          )?.[0]?.real_time ?? {}
        )
      : [];
    const numberOfDataTicksOnGraphBetweenFromAndTo =
      (to * 60) / 5 - (from * 60) / 5 + 1;

    return {
      options: {
        maintainAspectRatio: false,
        responsive: true,
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 12,
          },
        },
        tooltips: {
          enabled: true,
          mode: "index",
          callbacks: {
            title: (tooltipItems, data) =>
              `${
                intl.messages[
                  "pedestrianStatisticsScene.chart.tooltipTitlePrefix"
                ]
              } ${get(data, "labels", {})[tooltipItems[0].index]}`,
            label: (
              tooltipItem = { datasetIndex: null, index: null },
              data = { datasets: [] }
            ) => {
              const { datasetIndex, index } = tooltipItem;
              const { datasets } = data;
              const currentDataset =
                datasetIndex >= 0 && datasets[datasetIndex];
              const nbPedestrians =
                currentDataset &&
                currentDataset.data &&
                currentDataset.data[index];

              return `${datasets[datasetIndex].label}: ${nbPedestrians} ${
                intl.messages[
                  `pedestrianStatisticsScene.chart.pedestrian${
                    nbPedestrians > 1 ? "s" : ""
                  }`
                ]
              }`;
            },
          },
        },
        scales: {
          xAxes: [
            {
              categoryPercentage: 1.0,
              barPercentage: 0.95,
              ticks: {
                autoSkip: true,
                maxTicksLimit: 10,
              },
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                min: 0,
                callback: function (label, index, labels) {
                  if (Math.floor(Number(label)) === Number(label)) {
                    return label;
                  }
                },
              },
            },
          ],
        },
      },
      data: {
        toggle: Abilities.ability.can(
          "read",
          "pedestrian_stats.queue.dailyAttendanceMax"
        ),
        labels: isHourModeSelected
          ? Array.from({ length: 24 }, (_, x) => `${x}h`)
          : // Calculation to display the correct time range on the x-axis based on filters
            Array.from(
              { length: numberOfDataTicksOnGraphBetweenFromAndTo },
              (_, x) => {
                return moment()
                  .hours(from)
                  .minutes(x * 5)
                  .format("HH:mm");
              }
            ),
        datasets: [
          ...Object.values(
            pedestrianStatistics?.queue?.dailyAttendanceMax?.data ?? {}
          ).map((data) => {
            if (!data) return null;
            const [key] = Object.entries(data).flat();
            const area = pedestrianStatistics.map.selectedLayers.find(
              (area) => area.area_id === key
            );
            const color =
              area?.options.selection_color ||
              MapConfig?.flow?.point?.fillColor?.selected;

            let realTime = isHourModeSelected
              ? Object.values(data[key].real_time)
              : this.getDataByFiveMinutesRange(data[key].real_time, from, to);

            return {
              type: "line",
              label: area?.long_name ?? key,
              data: realTime,
              fill: false,
              fillOpacity: 0.4,
              borderWidth: 2,
              borderColor: color,
              backgroundColor: color,
              pointBorderColor: color,
              pointBackgroundColor: color,
              pointHoverBackgroundColor: color,
              pointHoverBorderColor: color,
            };
          }),
          isOnlyOneQueueAreaSelected
            ? {
                label:
                  intl.messages["pedestrianStatisticsScene.chart.previsional"],
                data: isHourModeSelected
                  ? Object.values(
                      Object.values(
                        pedestrianStatistics.queue.dailyAttendanceMax.data[0]
                      )[0].previsional
                    )
                  : this.getDataByFiveMinutesRange(
                      Object.values(
                        pedestrianStatistics?.queue?.dailyAttendanceMax
                          ?.data?.[0] ?? {}
                      )?.[0]?.previsional ?? {},
                      from,
                      to
                    ),
                borderColor: ParkkiTheme.colors.turquoise,
                backgroundColor:
                  PedestrianStatisticsUtils.getBackgroundColorArray(
                    isHourModeSelected
                      ? realTimeValuesUnfiltered.filter((x) => x !== null)
                          .length - 1
                      : realTimeValuesUnfiltered.filter((x) => x !== null)
                          .length -
                          (from * 60) / 5, // Gives us the number of data ticks passed since the current hour
                    isHourModeSelected ? "HOUR" : "5MIN"
                  ),
              }
            : null,
        ].filter((x) => x !== null),
      },
    };
  };

  static getRealTimeDailyAttendanceArray = (
    pedestrianStatisticsState,
    attendanceLimitTime
  ) => {
    if (
      !pedestrianStatisticsState ||
      !pedestrianStatisticsState.dailyAttendance ||
      !pedestrianStatisticsState.dailyAttendance.real_time
    )
      return [];
    return CommonUtils.formatGraphWithTimeLimit(
      pedestrianStatisticsState.dailyAttendance.real_time,
      attendanceLimitTime
    );
  };

  static getPrevisionalDailyAttendanceArray = (
    pedestrianStatisticsState,
    attendanceLimitTime
  ) => {
    if (
      !pedestrianStatisticsState ||
      !pedestrianStatisticsState.dailyAttendance ||
      !pedestrianStatisticsState.dailyAttendance.previsional
    )
      return [];
    return CommonUtils.formatGraphWithTimeLimit(
      pedestrianStatisticsState.dailyAttendance.previsional,
      attendanceLimitTime
    );
  };

  /**
   * Retrieves the label background color based on the area condition.
   *
   * @param {Object} pedestrianStatistics - The pedestrian statistics state.
   * @param {Object} areaConfig - The area backoffice settings.
   * @param {Object | null} background - The background object - If null it will create the background object
   * @param {Object} area - The area object.
   * @returns {string} The label background color.
   */
  static getLabelBackgroundColorByAreaCondition(
    pedestrianStatistics,
    areaConfig,
    background,
    area
  ) {
    let backgroundToUse = background;

    if (backgroundToUse === null) {
      const formattedOptions = pedestrianStatistics.details?.items?.find(
        (item) => {
          if (item?.reqFilters) {
            return item.area_id === area.area_id;
          }

          return item.area_id === area.area_id;
        }
      );

      backgroundToUse = formattedOptions?.background;
    }

    const areaData = pedestrianStatistics.queue?.data?.find(
      (x) => x.area === area.area_id
    );

    if (!areaData) {
      return null;
    }

    const condition =
      PedestrianStatisticsUtils.getBackofficeSettingsConditionWithValue(
        pedestrianStatistics.config?.queue_management,
        area.area_id,
        areaData ? areaData.waiting_time_in_seconds / 60 : 0
      );
    const conditionFoundIndex = areaConfig.graph_config.yAxis?.findIndex(
      (graphCondition) =>
        graphCondition.symbol === condition.symbol &&
        graphCondition.value === condition.value
    );
    const areaConditionPercentage =
      PedestrianStatisticsUtils.getLabelBackgroundFormattedPercentage({
        value: conditionFoundIndex,
        arrayLength: areaConfig.graph_config.yAxis.length - 1,
      });
    const valueColors = Object.keys(backgroundToUse?.config?.valueColors ?? {});
    const color =
      backgroundToUse?.config?.valueColors?.[
        CommonUtils.getClosestLowerValue(
          valueColors.map((x) => Number(x)),
          areaConditionPercentage
        ) ?? 0
      ];

    if (color) {
      return color.backgroundColor;
    }

    return null;
  }

  /**
   * Calculates the formatted percentage for the label background.
   * Used for queue areas(point) label on map.
   * @param value - The value to calculate the percentage for.
   * @param arrayLength - The length of the array.
   * @returns The formatted percentage for the label background.
   */
  static getLabelBackgroundFormattedPercentage({ value, arrayLength }) {
    const maxValue = 100;

    if ((arrayLength === 1 || arrayLength === 2) && value === 0) {
      return 33;
    }

    // This permits to return the smallest value to always have a minimum of green bar
    if (value === 0) {
      return (1 / arrayLength) * maxValue;
    }
    if (arrayLength === 2 && value === 1) {
      return 51;
    }

    return (value / arrayLength) * maxValue;
  }

  static formatDetailsStatsFromApi = (data = {}, queueAreas = []) => {
    const areas = data?.areas || data?.data || [];

    return {
      ...data,
      items: areas.map((item) => {
        const isAreaOfTypeQueue = queueAreas.includes(item.area_id);

        return {
          ...item,
          background: {
            config: ParkkiTheme.parkStatisticsAttendanceBar,
            value: item?.value ?? item?.stats?.value,
            // 100 is the max value for the percentage bar background of a queue area
            total: isAreaOfTypeQueue ? 100 : item?.stats?.capacity,
          },
        };
      }),
    };
  };

  static formatAreasFromApi = (areas = [], config = []) => {
    const areasToDisplay = areas.filter((area) =>
      config.find(
        (areaConfig) => areaConfig?.area_id && areaConfig?.area_id === area?.id
      )
    );

    return areasToDisplay.map((area) =>
      PedestrianStatisticsUtils.formatAreaFromApi(area, config)
    );
  };

  static formatAreaFromApi = (area, config = []) => {
    const areaConfig = config.find(
      (areaConf) =>
        get(area, "id", get(area, "area_id")) ===
        get(areaConf, "area_id", get(areaConf, "id"))
    );

    return {
      ...areaConfig,
      ...area,
      apiType: area?.type,
      type: "area",
      options: {
        ...get(area, "options", {}),
        ...get(areaConfig, "display", {}),
      },
    };
  };

  static formatLayersInfosFromApi = (layersInfos, config) => {
    return {
      ...layersInfos,
      areas: PedestrianStatisticsUtils.formatAreasFromApi(
        get(layersInfos, "areas", []),
        [...get(config, "areas", []), ...get(config, "queue_areas", [])]
      ),
    };
  };

  static getDefaultSelectedLayers = (map) => {
    const selectedLayers = get(map, "areas", [])
      .map((area) => {
        if (get(area, "options.selected_by_default") === true) {
          return {
            ...area,
            type: "area",
          };
        }

        return undefined;
      })
      .filter((item) => item);

    return selectedLayers;
  };

  static getSelectedLayers = (pedestrianStatisticsState) =>
    get(pedestrianStatisticsState, "map.selectedLayers", []);

  static getKeyNumbersStatsRow = () => {
    if (!Abilities.ability.can("read", "pedestrian_stats.keys")) return null;
    const intl = IntlUtils.getIntlInstance();

    return [
      {
        toggle: Abilities.ability.can("read", "pedestrian_stats.keys"),
        label:
          intl.messages["pedestrianStatisticsScene.statsRow.keyNumbersLabel"],
        subLabel:
          intl.messages["pedestrianStatisticsScene.statsRow.insideZone"],
        type: {
          name: "label",
          color: "turquoise",
        },
      },
      {
        toggle: Abilities.ability.can(
          "read",
          "pedestrian_stats.keys.visitorsCounter"
        ),
        label: (
          <FormattedMessage
            id="pedestrianStatisticsScene.statsRow.visitorsLabel"
            values={{
              count: GlobalFiltersUtils.getPedestrianStatsByKey(
                "general",
                "stats"
              )?.value,
            }}
          />
        ),
        infos:
          intl.messages["pedestrianStatisticsScene.statsRow.infos.visitors"],
        value: GlobalFiltersUtils.getPedestrianStatsByKey("general", "stats"),
        type: {
          toggle: Abilities.ability.can(
            "read",
            "pedestrian_stats.keys.visitorsCounter.avg"
          ),
          name: "normal",
          order: "first",
          retrieveNormal: PedestrianStatisticsUtils.getNormalAbsoluteValue,
          way: PedestrianStatisticsUtils.getWay,
          retrieveColor: PedestrianStatisticsUtils.getColor,
          symbol: "arrow",
          unity: "%",
        },
      },
      {
        toggle: Abilities.ability.can("read", "pedestrian_stats.keys.flow"),
        label: intl.messages["pedestrianStatisticsScene.statsRow.flow"],
        subLabel:
          intl.messages["pedestrianStatisticsScene.statsRow.duringCurrentHour"],
        infos: intl.messages["pedestrianStatisticsScene.statsRow.infos.flow"],
        value: GlobalFiltersUtils.getPedestrianStatsByKey(
          "general",
          "hourly_variation"
        ),
        type: {
          name: "normalWithoutLabel",
          order: "last",
          way: PedestrianStatisticsUtils.getWayByValue,
          retrieveColor: PedestrianStatisticsUtils.getColorByValue,
          retrieveNormal: PedestrianStatisticsUtils.getAbsValue,
          symbol: "arrow",
          unity: " %",
        },
      },
    ];
  };

  static getLineChartData = (state, type, accessor, size) => {
    return new Array(size).fill(get(state, `${type}.${accessor}`));
  };

  static getAttendanceLimitTime = (state) => {
    return (
      state?.config?.attendance ?? {
        left_time_limit: 0,
        right_time_limit: 24,
      }
    );
  };

  static getHourRange = (pedestrianStatistics, type) => {
    return [
      Math.round(get(pedestrianStatistics, `filters.${type}.fromHour`) / 3600),
      Math.round(get(pedestrianStatistics, `filters.${type}.toHour`) / 3600),
    ];
  };

  static getChartStats = (pedestrianStatisticsState) => {
    const isHourModeSelected =
      pedestrianStatisticsState?.filters
        ?.isPedestrianAffluenceChartTimeRangeByHour === true;

    const fromHour = pedestrianStatisticsState?.filters?.range?.fromHour / 3600;
    const toHour = pedestrianStatisticsState?.filters?.range?.toHour / 3600;

    const intl = IntlUtils.getIntlInstance();
    const attendanceLimitTime =
      PedestrianStatisticsUtils.getAttendanceLimitTime(
        pedestrianStatisticsState
      );

    let attendanceRealTime = !isHourModeSelected
      ? pedestrianStatisticsState?.dailyAttendance?.real_time
      : PedestrianStatisticsUtils.getRealTimeDailyAttendanceArray(
          pedestrianStatisticsState,
          attendanceLimitTime
        );

    let attendancePrevisionalTime = !isHourModeSelected
      ? pedestrianStatisticsState?.dailyAttendance?.previsional
      : PedestrianStatisticsUtils.getPrevisionalDailyAttendanceArray(
          pedestrianStatisticsState,
          attendanceLimitTime
        );

    if (!isHourModeSelected) {
      attendanceRealTime = Object.keys(attendanceRealTime).reduce(
        (currentResult, currentKey) => {
          const currentKeyHours = parseInt(currentKey?.split(":")[0], 10);
          const currentKeyMinutes = parseInt(currentKey?.split(":")[1], 10);

          if (
            (currentKeyHours >= fromHour && currentKeyHours < toHour) ||
            (currentKeyHours === toHour && currentKeyMinutes === 0)
          ) {
            return {
              ...currentResult,
              [currentKey]:
                pedestrianStatisticsState?.dailyAttendance?.real_time?.[
                  currentKey
                ],
            };
          }

          return currentResult;
        },
        {}
      );
      attendancePrevisionalTime = Object.keys(attendancePrevisionalTime).reduce(
        (currentResult, currentKey) => {
          const currentKeyHours = parseInt(currentKey?.split(":")[0], 10);
          const currentKeyMinutes = parseInt(currentKey?.split(":")[1], 10);

          if (
            (currentKeyHours >= fromHour && currentKeyHours < toHour) ||
            (currentKeyHours === toHour && currentKeyMinutes === 0)
          ) {
            return {
              ...currentResult,
              [currentKey]:
                pedestrianStatisticsState?.dailyAttendance?.previsional?.[
                  currentKey
                ],
            };
          }

          return currentResult;
        },
        {}
      );
    }

    const labels = Object.keys(attendanceRealTime).map((v) =>
      isHourModeSelected ? `${v}h` : v
    );

    return {
      toggle: Abilities.ability.can("read", "pedestrian_stats.dailyAttendance"),
      labels,
      datasets: [
        {
          type: "line",
          label: intl.messages["parkStatisticsScene.chart.capacity"],
          yLabel: "capacity",
          data: PedestrianStatisticsUtils.getLineChartData(
            pedestrianStatisticsState,
            "dailyAttendance",
            "capacity",
            Object.values(attendanceRealTime).length
          ),
          fill: false,
          borderColor: ParkkiTheme.colors.grey,
          backgroundColor: "rgba(255, 255, 255, 0)",
          borderDash: [5, 5],
          pointHoverRadius: 0,
          pointHoverBorderWidth: 0,
          pointRadius: 0,
          borderWidth: 1,
        },
        {
          type: "line",
          label: intl.messages["pedestrianStatisticsScene.chart.realTime"],
          data: Object.values(attendanceRealTime),
          fill: false,
          borderColor: ParkkiTheme.colors.red,
          backgroundColor: ParkkiTheme.colors.red,
          pointBorderColor: ParkkiTheme.colors.red,
          pointBackgroundColor: ParkkiTheme.colors.red,
          pointHoverBackgroundColor: ParkkiTheme.colors.red,
          pointHoverBorderColor: ParkkiTheme.colors.red,
        },
        {
          type: "bar",
          label: intl.messages["pedestrianStatisticsScene.chart.previsional"],
          data: Object.values(attendancePrevisionalTime),
          backgroundColor: PedestrianStatisticsUtils.getBackgroundColorArray(
            get(
              Object.values(attendanceRealTime).filter(
                (item) => item !== null && item !== undefined
              ),
              "length",
              0
            ) - 1,
            pedestrianStatisticsState?.filters
              ?.isPedestrianAffluenceChartTimeRangeByHour
              ? "HOUR"
              : "5MIN"
          ),
          borderColor: ParkkiTheme.colors.turquoise,
          hoverBackgroundColor: ParkkiTheme.colors.turquoise,
          hoverBorderColor: ParkkiTheme.colors.turquoise,
        },
      ],
    };
  };

  static getStatsSlider = (pedestrianStatisticsState, intl) => {
    const valueInPercentage = Math.round(
      (pedestrianStatisticsState?.general?.stats?.value /
        pedestrianStatisticsState?.general?.stats?.capacity) *
        100
    );
    const formattedMarks =
      pedestrianStatisticsState?.config?.thresholds?.map((threshold) => ({
        value: threshold?.percentage,
        label: threshold?.label,
      })) || [];

    return {
      toggle: Abilities.ability.can("read", "pedestrian_stats.attendance"),
      marks: [
        ...formattedMarks,
        {
          value: 100,
          label: intl.formatMessage(
            { id: "pedestrianStatisticsScene.slider.capacity" },
            { capacity: pedestrianStatisticsState?.general?.stats?.capacity }
          ),
        },
      ],
      data: [
        0,
        ...formattedMarks.map((item) => item.value),
        valueInPercentage,
      ].sort(),
      mainValue: valueInPercentage,
    };
  };

  static getAffluenceChartOptions = (pedestrianStatistics) => {
    const intl = IntlUtils.getIntlInstance();
    const isHourModeSelected =
      pedestrianStatistics?.filters
        ?.isPedestrianAffluenceChartTimeRangeByHour === true;

    return {
      maintainAspectRatio: false,
      responsive: true,
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 12,
        },
      },
      tooltips: {
        enabled: true,
        mode: "index",
        callbacks: {
          title: (tooltipItems, data) =>
            `${
              intl.messages[
                "pedestrianStatisticsScene.chart.tooltipTitlePrefix"
              ]
            } ${get(data, "labels", {})[tooltipItems[0].index]}`,
          label: (
            tooltipItem = { datasetIndex: null, index: null },
            data = { datasets: [] }
          ) => {
            const { datasetIndex, index } = tooltipItem;
            const { datasets } = data;
            const currentDataset = datasetIndex >= 0 && datasets[datasetIndex];
            const nbPedestrians =
              (currentDataset &&
                currentDataset.data &&
                currentDataset.data[index]) ||
              intl.messages["pedestrianStatisticsScene.chart.any"];

            return `${datasets[datasetIndex].label}: ${nbPedestrians} ${
              intl.messages[
                `pedestrianStatisticsScene.chart.pedestrian${
                  nbPedestrians > 1 ? "s" : ""
                }`
              ]
            }`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            categoryPercentage: 1.0,
            barPercentage: 0.95,
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10,
            },
            gridLines: {
              display: false,
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
    };
  };

  static getBackgroundColorArray = (realTimeLength, xAxisType) => {
    const defaultBarColor = ParkkiTheme.colors.grey;
    const otherColor = ParkkiTheme.colors.turquoise3;

    const maxData = xAxisType === "HOUR" ? 24 : 288;

    return Array(maxData)
      .fill()
      .map((v, i) => (i <= realTimeLength ? defaultBarColor : otherColor));
  };

  static getFilters = (type = "default", state) => {
    const { radius, position } = state.GlobalFilters;
    const { areaType, timestamp } = state.PedestrianStatistics?.filters;
    const currentFloor = MapUtils.getFloor(state.Map);
    let data = {};
    let filters = {};

    if (get(areaType, "length", 0) > 0) {
      filters = {
        area: {
          "type ($IN)": areaType,
        },
      };
    }

    let xAxis = "HOUR";
    if (
      state.PedestrianStatistics?.filters
        ?.isPedestrianAffluenceChartTimeRangeByHour === undefined
    ) {
      xAxis = "HOUR";
    } else {
      xAxis =
        state.PedestrianStatistics?.filters
          ?.isPedestrianAffluenceChartTimeRangeByHour === true
          ? "HOUR"
          : "5MIN";
    }

    if (
      type === "radius" &&
      currentFloor !== null &&
      currentFloor !== undefined
    ) {
      filters = {
        ...filters,
        area: { ...get(filters, "area", {}), floor_level: currentFloor },
      };
    }

    if (type === "areas") {
      const areas = this.getSelectedLayersFromCurrentFloorLevel(
        state,
        currentFloor
      );

      if (areas.length > 0) {
        data = { ...data, areas };
      }
    }

    if (type === "areasDetails") {
      const areas = state.PedestrianStatistics?.map?.areas
        .filter(
          (layer) =>
            currentFloor === undefined ||
            currentFloor === null ||
            get(layer, "floor_level") === currentFloor
        )
        .map((layer) => get(layer, "id"));

      if (areas.length > 0) {
        data = { ...data, areas };
      }
    }

    if (type === "radius") {
      data = { ...data, radius, position };
    }

    return {
      ...data,
      filters,
      timestamp,
      x_axis: xAxis,
    };
  };

  static getSelectedLayersFromCurrentFloorLevel = (
    state,
    currentFloorLevel
  ) => {
    const globalSelectedLayers =
      PedestrianStatisticsUtils.getSelectedLayers(state.PedestrianStatistics) ||
      [];

    return globalSelectedLayers
      .filter(
        (layer) =>
          currentFloorLevel === undefined ||
          currentFloorLevel === null ||
          get(layer, "floor_level") === currentFloorLevel
      )
      .map((selectedLayer) => get(selectedLayer, "id"));
  };

  static getAreasFromCurrentFloor = (state, currentFloorLevel) => {
    const currentFloor =
      currentFloorLevel !== null && currentFloorLevel !== undefined
        ? currentFloorLevel
        : MapUtils.getFloor(state.Map);
    const areas = state.PedestrianStatistics?.map?.areas || [];

    return areas
      .filter((area) => area?.type !== "UNEQUIPPED")
      .filter(
        (area) =>
          currentFloor === undefined ||
          currentFloor === null ||
          get(area, "floor_level") === currentFloor
      );
  };

  static formatAreasFromCurrentFloor = (state) => {
    const currentFloor = MapUtils.getFloor(state.Map);
    const selectedLayersFromCurrentFloor =
      this.getSelectedLayersFromCurrentFloorLevel(state, currentFloor);
    const currentFloorAreas = this.getAreasFromCurrentFloor(
      state,
      currentFloor
    );

    return currentFloorAreas?.map((area) => ({
      ...area,
      selected: selectedLayersFromCurrentFloor?.indexOf(area?.id) >= 0,
    }));
  };

  /**
   * This function returns the condition from the backoffice settings that matches the value
   * @param {Array<QueueManagementOptions>} boSettings
   * @param {string} areaId
   * @param {number} value
   * @returns {object} - The condition that matches the value { symbol: string, value: number }
   */
  static getBackofficeSettingsConditionWithValue = (
    boSettings,
    areaId,
    value
  ) => {
    const settings = boSettings.find((s) => s.area_id === areaId);

    const res = settings?.graph_config.yAxis.find((condition) => {
      if (condition.symbol === "<" && value < condition.value) {
        return condition;
      } else if (condition.symbol === ">" && value >= condition.value) {
        return condition;
      }
      return false;
    });

    return res;
  };
}

export default PedestrianStatisticsUtils;
